import { ChangeEvent, CSEPhoneNumberInput } from '@cloudsuite/mosaic-elements'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { BasketContact, BasketHasNoContact } from 'components/ordering/acl/internal-types.js'
import { User } from 'components/ordering/multiple-baskets/basket-api/user.js'
import { parseBasketContactForm } from 'components/ordering/multiple-baskets/basket-contact/basket-contact-form-parser.js'
import {
  LOCALLY_STORED_BASKET_CONTACT,
  LocallyStoredBasketContact,
} from 'components/ordering/multiple-baskets/basket-contact/locally-stored-basket-contact.js'
import {
  ORDERING_STORED_BASKET_CONTACT,
  OrderingStoredBasketContact,
} from 'components/ordering/multiple-baskets/basket-contact/odering-stored-basket-contact.js'
import { BasketContactFormSubmitted } from 'components/ordering/multiple-baskets/basket-events.js'
import {
  BASKET_LIST_MUTATOR,
  BASKET_LIST_STORE,
  BasketListMutator,
  BasketListStore,
} from 'components/ordering/multiple-baskets/basket-store/basket-list-store.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { is } from 'typia'
import { style } from './basket-contact.style.js'


@customElement('csc-basket-contact')
export class CSCBasketContact extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @query('form')
  private form!: HTMLFormElement

  @query('cse-phone-number-input')
  private phoneNumberInput!: CSEPhoneNumberInput

  @inject(BASKET_LIST_MUTATOR)
  private basketListMutator!: BasketListMutator

  @inject(BASKET_LIST_STORE)
  private basketListStore!: BasketListStore

  @inject(LOCALLY_STORED_BASKET_CONTACT)
  private locallyStoredBasketContact!: LocallyStoredBasketContact

  @inject(ORDERING_STORED_BASKET_CONTACT)
  private orderingStoredBasketContact!: OrderingStoredBasketContact

  @state()
  private basketId?: string

  @state()
  private contact: BasketContact = {
    name: '',
    telephone: '',
  }

  @property({ type: Array, attribute: 'telephone-country-codes' })
  private telephoneCountryCodes: string[] = []

  @property({ attribute: 'ordering-endpoint' })
  private orderingEndpoint = ''

  @property({ type: Number, attribute: 'shop-id' })
  private shopId?: number

  @property({ type: Number, attribute: 'party-id' })
  private partyId?: number

  @property({ type: Number, attribute: 'user-id' })
  private userId?: number

  private get user(): User {
    const maybeUser = { shopId: this.shopId, partyId: this.partyId, userId: this.userId }
    if (is<User>(maybeUser)) { return maybeUser }
    throw new Error('user-id, party-id and shop-id are mandatory.')
  }

  override async connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      const selectedBasket = this.basketListStore.selectedBasket()
      if (selectedBasket === null) { return }

      this.basketId = selectedBasket.basketId
      this.orderingStoredBasketContact.initialize(this.orderingEndpoint, this.basketId)
      this.updateBasketContactFromStorage()
    })

    this.basketListMutator.initialize(this.user)
  }

  private storeBasketContactLocally = true
  private updateBasketContactFromStorage = async () => {
    const orderingContact = await this.orderingStoredBasketContact.getBasketContact()

    if (!(orderingContact instanceof BasketHasNoContact)) {
      this.contact = orderingContact
      this._setPhoneNumber()
      this.storeBasketContactLocally = false
    } else {
      this.contact = this.locallyStoredBasketContact.contact
      this._setPhoneNumber()
      this.storeBasketContactLocally = true
    }
  }

  private _setPhoneNumber = () => {
    this.phoneNumberInput.value = this.contact.telephone
  }

  submit = async () => {
    if (this.basketId === undefined) {
      throw new Error('csc-basket-contact not fully initialized: missing basketId.')
    }

    const basketContact = parseBasketContactForm(this.form)
    const nameValid = this.validateNameField(basketContact.name)
    const telephoneValid = this.phoneNumberInput.validateTelephoneField(basketContact.telephone)
    this.phoneInvalid = !telephoneValid

    const formValid = nameValid && telephoneValid
    if (!formValid) {
      super.dispatchEvent(new BasketContactFormSubmitted(false))
      return
    }

    this.contact = { name: basketContact.name, telephone: this.phoneNumberInput.value ?? basketContact.telephone }

    await this.orderingStoredBasketContact.addBasketContact(this.contact)
    if (this.storeBasketContactLocally) {
      this.locallyStoredBasketContact.contact = this.contact
    }
    super.dispatchEvent(new BasketContactFormSubmitted(true))
  }

  @state()
  private nameInvalid: boolean = false

  private validateNameField = (value: any): boolean => {
    const valid = typeof value === 'string' && value.length >= 1
    this.nameInvalid = !valid

    return valid
  }

  private handleNameChanged = (event: ChangeEvent) => {
    this.validateNameField(event.value)
  }

  private renderNameField = (): TemplateResult => {
    return html`
      <cse-form-field id="field-name">
        <cse-label
          for="contact-name"
          slot="label">
          ${t('name_label', { ns: 'csc-basket-contact', default: 'Name' })}
        </cse-label>
        <cse-text-input
          id="contact-name"
          name="name"
          type="text"
          value="${this.contact.name}"
          valid="${this.nameInvalid ? 'error' : 'none'}"
          @change="${this.handleNameChanged}">
        </cse-text-input>
        ${this.nameInvalid
          ? html`
            <div slot="invalid-text">
              ${t('name_error', { ns: 'csc-basket-contact', default: 'This name is not valid' })}
            </div>`
          : nothing
        }
      </cse-form-field>`
  }

  @state()
  private phoneInvalid: boolean = false

  private handlePhoneChanged = () => {
    this.phoneInvalid = Boolean(this.phoneNumberInput?.validity.badInput)
  }

  private renderPhoneField = (): TemplateResult => {
    return html`
      <cse-form-field id="field-telephone">
        <cse-label
          for="contact-telephone"
          slot="label">
          ${t('telephone_label', { ns: 'csc-basket-contact', default: 'Phone number' })}
        </cse-label>
        <cse-phone-number-input
          id="contact-telephone"
          name="telephone"
          telephone-country-codes="${ JSON.stringify(this.telephoneCountryCodes) }"
          placeholder="${t('telephone_placeholder', { ns: 'csc-basket-contact', default: '+31612345678' })}"
          @change="${this.handlePhoneChanged}">
        </cse-phone-number-input>
        ${this.phoneInvalid
          ? html`
            <div slot="invalid-text">
              ${t('telephone_error', { ns: 'csc-basket-contact', default: 'This phone number is not valid' })}
            </div>`
          : nothing
        }
      </cse-form-field>`
  }

  override render(): TemplateResult {
    return html`
      <cse-text
        as="h3"
        variant="heading-3">
        ${t('form_heading', { ns: 'csc-basket-contact', default: 'Contact' })}
      </cse-text>
      <form class="form">
        <cse-fieldset>
          ${this.renderNameField()}
          ${this.renderPhoneField()}
        </cse-fieldset>
      </form>`
  }
}
